import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, takeUntil } from 'rxjs';
import { v4 as uuid4 } from 'uuid';
import { environment } from '../../../environments/environment.development';
import { LoginPayload } from '../../interfaces/auth.interface';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.html',
  styleUrls: ['./login.scss'],
})
export class Login implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('usrIn') usrInRef!: ElementRef<HTMLInputElement>;
  @ViewChild('pwdIn') pwdInRef!: ElementRef<HTMLInputElement>;
  form: FormGroup;
  loading = false;
  step = 0;
  showPassword = false;
  private _origin = '';
  private _callbackUrl = '';
  private _unsubscribeAll = new Subject();
  view: 'login' | 'forgot' | 'restricted' = 'login';
  authError: string | null = null;
  authErrorType: 'field' | 'page' | null = null;

  constructor(
    private _fb: FormBuilder,
    private _authServ: AuthService,
    private _toastrServ: ToastrService,
    private _route: ActivatedRoute,
  ) {
    this.form = this._fb.nonNullable.group({
      email: this._fb.nonNullable.control('', [Validators.required]),
      password: this._fb.nonNullable.control(''),
    });
  }

  get email() {
    return this.form.get('email') as FormControl;
  }

  get password() {
    return this.form.get('password') as FormControl;
  }

  ngOnInit(): void {
    this._route.queryParams
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((params) => {
        this._callbackUrl =
          params?.['callbackUrl'] || environment.web_url + '/app/auth/callback';
        if (!window.parent || window.parent == window) {
          this._origin = 'localhost';

          const currentUser = this._authServ.currentUser;
          const users = this._authServ.users;
          if (currentUser && users) {
            const user = users[currentUser];
            window.location.replace(
              `${this._callbackUrl}${this._authServ.serializeData({
                refreshToken: user.refreshToken,
                token: user.token,
              })}`,
            );
          }
        }
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.usrInRef.nativeElement.focus(), 0);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  goBack() {
    this.step = 0;
    this.email.setValidators([Validators.required]);
    this.password.clearValidators();
    this.password.reset();
    this.password.setErrors(null);
    setTimeout(() => this.usrInRef.nativeElement.focus(), 0);
  }

  handleSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid && !this.email.errors?.['invalid']) return;
    const values = this.form.getRawValue();
    this.loading = true;
    if (this.step == 0) this.checkUser(values.email);

    if (this.step == 1) this.login(values);
  }
  checkUser(email: string) {
    this._authServ
      .checkByUsername(email)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          if (res) {
            this.email.clearAsyncValidators();
            this.password.setValidators([Validators.required]);
            this.password.reset();
            this.step = 1;
            setTimeout(() => this.pwdInRef.nativeElement.focus(), 10);
          } else {
            this.email.setErrors({
              invalid: true,
            });
          }
        },
        error: (er) => {
          this.loading = false;

          if (er.status === 500) {
            this._toastrServ.error('Something went wrong while finding user.');
            return;
          }

          const message = er.error?.error;
          if (!message) return;

          if (message.toLowerCase().includes('ip')) {
            this.authError = message;
            this.view = 'restricted';
            return;
          }

          if (message.toLowerCase().includes('not found')) {
            this.email.setErrors({ invalid: true });
            return;
          }

          this.authError = message;
          this.authErrorType = 'field';
        },
      });
  }
  login({ email, password }: typeof this.form.value) {
    const browserId = uuid4();
    const payload: LoginPayload = {
      email,
      password,
      currentDomain: this._origin,
      client: JSON.stringify({
        browserId,
        ...this._authServ.getBrowserAndOS(),
      }),
    };

    this.authError = null;
    this.authErrorType = null;

    this._authServ
      .login(payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res) => {
          this.grant(res.grantToken, browserId);
        },
        error: (er) => {
          this.loading = false;

          if (er.status === 500) {
            this._toastrServ.error(
              'Unable to authenticate user.',
              'Something went wrong',
            );
            return;
          }

          const message = er.error?.error;

          if (!message) return;

          if (message.toLowerCase().includes('ip')) {
            this.authError = message;
            this.view = 'restricted';
            return;
          }

          if (message.toLowerCase().includes('invalid')) {
            this.password.setErrors({ invalid: true });
            return;
          }

          if (message.toLowerCase().includes('not found')) {
            this.email.setErrors({ invalid: true });
            return;
          }

          this.authError = message;
          this.authErrorType = 'field';
        },
      });
  }
  grant(grant: string, browserId: string) {
    this._authServ
      .grant(grant)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res) => {
          this._authServ.addUser({
            browserId,
            refreshToken: res.refreshToken,
            user: res.user,
            token: res.token,
          });
          this.loading = false;
          // this.form.reset();
          // this.step = 0;

          window.location.replace(
            `${this._callbackUrl}${this._authServ.serializeData({
              refreshToken: res.refreshToken,
              token: res.token,
            })}`,
          );
        },
        error: (er) => {
          this.loading = false;
          console.warn(er);
          this._toastrServ.error(
            'Unable to authenticate user.',
            'Something went wrong.',
          );
        },
      });
  }

  // forgotPassword() {
  //   const email = this.email.value;

  //   if (!email) return;

  //   window.location.href = `/forgot-password?username=${encodeURIComponent(
  //     email
  //   )}`;
  // }

  forgotPassword() {
    const email = this.email.value;
    if (!email) return;

    this.view = 'forgot';
  }
}
